/* Common Imports */

import { styled, useMediaQuery, useTheme } from "@mui/system";
import React from "react";

/* Redux Imports */

import { ObjectId } from "mongodb";
import StateInterface from "../../../../redux-magic/state-interface";
import {
	setBusinessDashboardAnalyticsFilterGraphDurationXThunk,
	setBusinessDashboardAnalyticsFilterGraphTypeYThunk,
	setBusinessDashboardAnalyticsFilterListingIdThunk,
	setBusinessDashboardAnalyticsFilterListingTypeThunk,
	setBusinessDashboardAnalyticsGraphSourceThunk,
	setBusinessDashboardCurrentScreenThunk,
	setLoadingThunk,
} from "../../../../redux-magic/store";
import { Area, BHK, Price } from "../../../../redux-magic/sub-interfaces/sub-interfaces";

/* Component Imports */

import { IconButton, Menu, MenuItem, SvgIcon, Typography } from "@mui/material";
import { useRouter } from "next/router";
import DeleteListingPropertyModal from "../../../modals/PropertyModal/DeleteListingProperty";
import PropertyCardConfigAndContact from "./sub-components/PropertyCardConfig";
import PropertyCardCTA from "./sub-components/PropertyCardCTA";
import PropertyCardImage from "./sub-components/PropertyCardImage";
import PropertyCardLocation from "./sub-components/PropertyCardLocation";
import PropertyCardPrice from "./sub-components/PropertyCardPrice";
import PropertyCardTitle from "./sub-components/PropertyCardTitle";

/* Icon Imports */

import { EventOutlined, MoreVertOutlined, UpdateOutlined } from "@mui/icons-material";
import LeadsIcon from "/public/images/icons/business-dashboard/leads.svg";
import SavedIcon from "/public/images/icons/business-dashboard/saved.svg";
import SharesIcon from "/public/images/icons/business-dashboard/shares.svg";
import ViewsIcon from "/public/images/icons/business-dashboard/views.svg";

/* Library Function Imports */

import PrepareConfigString from "../../../../lib/PrepareConfigString";
import PreparePriceString from "../../../../lib/PreparePriceString";

/* Styled Components */

const PropertyLink = styled("a", {
	shouldForwardProp: (prop) => prop !== "source",
})<{ source: string }>(({ theme, source }) => ({
	textDecoration: "none",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	borderRadius: "0.75rem",
	width: "100%",
	color: theme.palette.mode == "dark" ? "#fff" : "#000",
	"&:hover": {
		textDecoration: "none",
	},
	[theme.breakpoints.up("md1190")]: {
		width: "49%",
	},
	/* 4k breakpoint 3840p */
	[theme.breakpoints.up(theme.breakpoints.values.xl + 1920)]: {
		width: "32.25%",
	},
}));

const PropertyCardContainer = styled("div", {
	shouldForwardProp: (prop) => prop !== "source" && prop !== "isEditor",
})<{ source: string; isEditor: boolean }>(({ theme, source, isEditor }) => ({
	background: theme.palette.background.paper,
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	borderRadius: "0.75rem",
	objectFit: "contain",
	width: "100%",
	padding: "0.75rem 0rem 0rem 0rem",
	gap: "0.75rem",
	scrollSnapAlign: "start",
	flexShrink: 1,
	flexGrow: 1,
	"@media (pointer: fine)": {
		transition: "all 0.2s ease",
		"&:hover": {
			// boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.12)",
			boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
			transform: "scale(1.01)",
		},
	},
}));

const PropertyCardContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	cursor: "pointer",
	padding: "0rem 0.75rem 0rem 0.75rem",
}));

const PropertyCardInfoContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	padding: "0rem 0rem 0rem 0.5rem",
	width: "100%",
	height: "100%",
	cursor: "pointer",
}));

const PropertyCardStatsContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	padding: "0rem 0.75rem 0rem 0.75rem",
	width: "100%",
	gap: "0.75rem",
	cursor: "pointer",
}));

const StatContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	background: "#fff7f0",
	borderRadius: "0.75rem",
	padding: "0.5rem 0.5rem 0.5rem 0.5rem",
	cursor: "pointer",
}));

const StatValue = styled(Typography)(({ theme }) => ({
	fontSize: "0.825rem",
	lineHeight: "0.825rem",
	fontWeight: 500,
	padding: "0.25rem 0rem 0rem 0rem",
}));

const StatTitle = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	lineHeight: "0.75rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
}));

const RowContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
}));

const DateText = styled(Typography)(({ theme }) => ({
	fontSize: "0.75rem",
	fontWeight: 400,
	color: theme.palette.text.secondary,
	[theme.breakpoints.up("md1190")]: {
		fontSize: "0.875rem",
	},
}));

const CustomMenu = styled(Menu)(({ theme }) => ({
	"& .MuiPaper-root": {
		borderRadius: "0.75rem",
		padding: " 0rem",
	},
	"& .MuiList-root": {
		width: "100%",
		padding: "0rem 0rem 0rem 0rem",
	},
}));

const CustomMenuItem = styled(MenuItem)(({ theme }) => ({
	fontSize: "0.875rem",
	width: "100%",
	padding: "0.5rem 0.75rem 0.5rem 0.75rem",
	minHeight: "2rem",
	fontWeight: 400,
	color: theme.palette.error.main,
}));

const SubTextContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	margin: "0.5rem 0rem 0rem 0rem",
	width: "100%",
	cursor: "pointer",
	gap: "0.5rem",
	/* ipad Mini - 768 */
	[theme.breakpoints.up(theme.breakpoints.values.sm + 167)]: {
		gap: "0.65rem",
	},
	/* 860 */
	[theme.breakpoints.up("sm860")]: {
		gap: "0.75rem",
	},
	/* 910 */
	[theme.breakpoints.up("sm910")]: {
		gap: "0.85rem",
	},
	/* 1190 ~~Desktop Layout Start~~ */
	[theme.breakpoints.up("md1190")]: {
		gap: "0.75rem",
	},
}));

const PropertyCard = ({
	_id,
	profile_context,
	session,
	dispatch,
	loading,
	slug,
	transaction_type,
	property_type,
	title,
	images,
	location,
	published_status,
	price,
	bhk,
	no_of_seats,
	area,
	created_by,
	assigned_to,
	isEditor,
	boosting,
	source,
	search_log_id,
	leads_count,
	views_count,
	shares_count,
	saved_count,
	posted_on,
	updated_on,
	show_only_listing_details,
	broadcastData,
	business_profile_id,
	propertyStatus,
	is_recently_broadcasted,
	is_broadcasted_before,
	time_left_to_broadcast_again,
	broadcastFeatureDiscoveryTracker,
	ai_boosts,
}: {
	_id: ObjectId | string;
	profile_context: StateInterface["profile_context"];
	session: StateInterface["redux_session"];
	dispatch: Function;
	loading: boolean;
	slug: string;
	transaction_type: string;
	property_type: Array<string>;
	title: string;
	images: string;
	published_status: string;
	location: string;
	developer: string;
	price: Price;
	bhk: BHK;
	no_of_seats: BHK;
	area: Area;
	created_by?: any;
	assigned_to?: StateInterface["single_business_profile"]["business_profile"]["assigned_to"];
	isEditor?: boolean | undefined;
	boosting: StateInterface["property"]["boost_information"];
	source?: string;
	search_log_id?: string | ObjectId;
	leads_count?: number;
	views_count?: number;
	shares_count?: number;
	saved_count?: number;
	posted_on: string | Date;
	updated_on: string | Date;
	show_only_listing_details: boolean;
	broadcastData?: StateInterface["broadcast_data"] | null;
	business_profile_id: string | ObjectId | null | undefined;
	propertyStatus: string;
	is_recently_broadcasted: boolean;
	is_broadcasted_before: boolean;
	time_left_to_broadcast_again: number;
	broadcastFeatureDiscoveryTracker: StateInterface["feature_discovery_tracker"]["broadcast"];
	ai_boosts: StateInterface["ai_boosts"];
}) => {
	const theme = useTheme();
	const router = useRouter();

	const isDesktop = useMediaQuery(theme.breakpoints.up("md1190"));

	const is4k = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.xl + 1920));

	/* Translations */

	const localLang = {
		leads: router.locale === "ar-AE" ? "التوجيهات" : router.locale === "kn-IN" ? "ಮಾರ್ಗದರ್ಶನ" : "Leads",
		views: router.locale === "ar-AE" ? "المشاهدات" : router.locale === "kn-IN" ? "ವೀಕ್ಷಣೆಗಳು" : "Views",
		shares: router.locale === "ar-AE" ? "المشاركات" : router.locale === "kn-IN" ? "ಹಂಚಿಕೆಗಳು" : "Shares",
		saves: router.locale === "ar-AE" ? "المحفوظة" : router.locale === "kn-IN" ? "ಉಳಿಸಲಾಗಿದೆ" : "Saves",
		posted_on: router.locale === "ar-AE" ? "نشرت في" : router.locale === "kn-IN" ? "ಪೋಸ್ಟ್ ಮಾಡಲಾಗಿದೆ" : "Posted on",
		updated_on:
			router.locale === "ar-AE" ? "تم التحديث في" : router.locale === "kn-IN" ? "ನವೀಕರಿಸಲಾಗಿದೆ" : "Updated on",
		edit: router.locale === "ar-AE" ? "تحرير" : router.locale === "kn-IN" ? "ಸಂಪಾದಿಸಿ" : "Edit",
		unpublish: router.locale === "ar-AE" ? "إلغاء النشر" : router.locale === "kn-IN" ? "ಅನ್ಪಬ್ಲಿಷ್" : "Unpublish",
		delete: router.locale === "ar-AE" ? "حذف" : router.locale === "kn-IN" ? "ಅಳಿಸಿ" : "Delete",
	};

	/*

	& Next, Let's define a local state for Delete Modal.

	*/

	const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);

	/*

	& Next, Let's define a local state to handle menu.

	*/

	const [anchorElMenu, setAnchorElMenu] = React.useState<null | HTMLElement>(null);
	const [openMenu, setOpenMenu] = React.useState(false);

	/* Close select dropdowns on scroll */

	React.useEffect(() => {
		const handleScroll = () => {
			setOpenMenu(false);
		};

		window.addEventListener("scroll", handleScroll);
		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, []);

	return (
		<React.Fragment>
			{/* Options Menu */}

			<CustomMenu
				id="basic-menu"
				anchorEl={anchorElMenu}
				open={openMenu}
				onClose={() => {
					setAnchorElMenu(null);
					setOpenMenu(false);
				}}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				disableScrollLock={true}
			>
				{session?.user?.dbUser?._id === created_by?.user_id._id ||
				session?.user?.dbUser?.access_level.includes("super_admin") ||
				(session?.user?.dbUser?.access_level.includes("admin") &&
					session?.user?.dbUser?.access_level.includes("business_profiles") &&
					assigned_to?.includes(session?.user?.dbUser?._id)) ? (
					<CustomMenuItem
						sx={{
							color: theme.palette.text.primary,
						}}
						onClick={() => {
							window.open(`/properties/${slug}`, "_self");
							setAnchorElMenu(null);
							setOpenMenu(false);
						}}
					>
						{localLang.edit}
					</CustomMenuItem>
				) : null}

				{published_status === "published" &&
				(session?.user?.dbUser?._id === created_by?.user_id._id ||
					session?.user?.dbUser?.access_level.includes("super_admin") ||
					(session?.user?.dbUser?.access_level.includes("admin") &&
						session?.user?.dbUser?.access_level.includes("business_profiles") &&
						assigned_to?.includes(session?.user?.dbUser?._id))) ? (
					<CustomMenuItem
						onClick={() => {
							window.open(`/properties/${slug}`, "_self");
							setAnchorElMenu(null);
							setOpenMenu(false);
						}}
					>
						{localLang.unpublish}
					</CustomMenuItem>
				) : null}

				{session?.user?.dbUser?._id === created_by?.user_id._id ||
				session?.user?.dbUser?.access_level.includes("super_admin") ||
				(session?.user?.dbUser?.access_level.includes("admin") &&
					session?.user?.dbUser?.access_level.includes("business_profiles") &&
					assigned_to?.includes(session?.user?.dbUser?._id)) ? (
					<CustomMenuItem
						onClick={() => {
							setOpenDeleteModal(true);
							setAnchorElMenu(null);
							setOpenMenu(false);
						}}
					>
						{localLang.delete}
					</CustomMenuItem>
				) : null}
			</CustomMenu>

			{/* Delete Modal */}

			<DeleteListingPropertyModal
				open={openDeleteModal}
				handleClose={() => {
					setOpenDeleteModal(false);
				}}
				creator_user_id={created_by?.userId}
				creator_business_profile_id={created_by?.business_profile_id}
				propertyId={_id}
				session={session ? session : null}
				isEditor={isEditor ? isEditor : false}
				profile_context={profile_context}
				dispatch={dispatch}
				property_type="property"
			/>

			{/* Property Card */}

			<PropertyLink
				source={source ? source : ""}
				href={
					!show_only_listing_details
						? `${process.env.PRODUCTION_URL || ""}loading.html?callbackUrl=${encodeURIComponent(
								`${process.env.PRODUCTION_URL || ""}${router.locale}/properties/${slug}${search_log_id ? `?search_log_id=${search_log_id}` : ""}`,
							)}`
						: "javascript:void(0);" // eslint-disable-line
				}
				rel="noopener"
				target="_self"
				referrerPolicy="no-referrer"
				title={title}
				style={{
					direction: router.locale === "ar-AE" ? "rtl" : "ltr",
					boxShadow: !show_only_listing_details ? "0px 4px 18px 0px rgba(0, 0, 0, 0.05)" : "none",
					width: !show_only_listing_details ? (isDesktop ? "49%" : is4k ? "32.25%" : "100%") : "100%",
				}}
				onClick={() => {
					dispatch(
						setLoadingThunk({
							source: "properties",
							loading: true,
						}),
					);
				}}
			>
				<PropertyCardContainer
					source={source ? source : ""}
					isEditor={isEditor !== undefined ? isEditor : false}
					sx={{
						padding: !show_only_listing_details ? "0.75rem 0rem 0rem 0rem" : "0rem 0rem 0rem 0rem",
					}}
				>
					{/* Property Details */}

					<PropertyCardContentContainer>
						{/* Image */}

						<PropertyCardImage
							images={images}
							loading={loading}
							transaction_type={transaction_type}
						/>

						<PropertyCardInfoContainer
							sx={{ padding: router.locale === "ar-AE" ? "0rem 0.5rem 0rem 0rem" : "0rem 0rem 0rem 0.5rem" }}
						>
							<RowContainer sx={{ width: "100%" }}>
								{/* Price */}

								<PropertyCardPrice
									loading={loading}
									price={price}
								/>

								{/* Options Menu */}

								{isEditor && !show_only_listing_details && !(session?.user?.dbUser?.platform_access_level?.permissions && !session?.user?.dbUser?.platform_access_level?.permissions?.can_manage_properties) ? (
									<IconButton
										disableRipple
										disableFocusRipple
										disableTouchRipple
										onClick={(event: any) => {
											event.preventDefault();
											setAnchorElMenu(event.currentTarget);
											setOpenMenu(true);
										}}
										sx={{
											padding: "0rem 0rem 0rem 0rem",
										}}
									>
										<MoreVertOutlined
											sx={{
												color: "#757575",
												fontSize: "1.25rem",
											}}
										/>
									</IconButton>
								) : null}
							</RowContainer>

							{/* Title */}

							<PropertyCardTitle
								loading={loading}
								title={title !== "" || undefined ? title : "Unknown Title"}
							/>

							<SubTextContainer>
								{/* Location */}

								<PropertyCardLocation
									loading={loading}
									location={location !== "" || undefined ? location : "Unknown Location"}
								/>

								{/* Config  */}

								<PropertyCardConfigAndContact
									loading={loading}
									config={PrepareConfigString(transaction_type, property_type, bhk, no_of_seats, area)}
									transaction_type={transaction_type ? transaction_type : ""}
									property_type={property_type ? property_type : []}
								/>
							</SubTextContainer>
						</PropertyCardInfoContainer>
					</PropertyCardContentContainer>

					{/* Property Stats */}

					{!show_only_listing_details ? (
						<PropertyCardStatsContainer>
							{/* Leads */}

							<StatContainer
								onClick={(e: any) => {
									e.preventDefault();
									e.stopPropagation();

									dispatch(setBusinessDashboardAnalyticsFilterListingTypeThunk("property"));
									dispatch(setBusinessDashboardAnalyticsFilterListingIdThunk(_id));
									dispatch(setBusinessDashboardAnalyticsFilterGraphTypeYThunk("leads"));
									dispatch(setBusinessDashboardAnalyticsFilterGraphDurationXThunk("all"));
									dispatch(setBusinessDashboardAnalyticsGraphSourceThunk("listing_leads"));
									dispatch(setBusinessDashboardCurrentScreenThunk("graph"));
									router.push(
										{
											pathname: router.pathname,
											query: {
												...router.query,
												screen: "graph",
											},
										},
										undefined,
										{ shallow: true },
									);
								}}
							>
								<SvgIcon
									component={LeadsIcon}
									inheritViewBox={false}
									viewBox="0 0 36 36"
									width={24}
									height={24}
									sx={{
										height: "1.5rem",
										width: "1.5rem",
										fill: "none",
										// /* iphone 5 - 320 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
										// 	height: "2rem",
										// 	width: "2rem",
										// },
										// /* iphone se - 375 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
										// 	height: "2.25rem",
										// 	width: "2.25rem",
										// },
									}}
								/>
								<StatValue variant="body2">{PreparePriceString(leads_count ? leads_count : 0)}</StatValue>
								<StatTitle variant="body2">{localLang.leads}</StatTitle>
							</StatContainer>

							{/* Views */}

							<StatContainer
								onClick={(e: any) => {
									e.preventDefault();
									e.stopPropagation();

									dispatch(setBusinessDashboardAnalyticsFilterListingTypeThunk("property"));
									dispatch(setBusinessDashboardAnalyticsFilterListingIdThunk(_id));
									dispatch(setBusinessDashboardAnalyticsFilterGraphTypeYThunk("views"));
									dispatch(setBusinessDashboardAnalyticsFilterGraphDurationXThunk("all"));
									dispatch(setBusinessDashboardAnalyticsGraphSourceThunk("listing_views"));
									dispatch(setBusinessDashboardCurrentScreenThunk("graph"));
									router.push(
										{
											pathname: router.pathname,
											query: {
												...router.query,
												screen: "graph",
											},
										},
										undefined,
										{ shallow: true },
									);
								}}
							>
								<SvgIcon
									component={ViewsIcon}
									inheritViewBox={false}
									viewBox="0 0 25 24"
									width={24}
									height={24}
									sx={{
										height: "1.5rem",
										width: "1.5rem",
										fill: "none",
										// /* iphone 5 - 320 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
										// 	height: "2rem",
										// 	width: "2rem",
										// },
										// /* iphone se - 375 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
										// 	height: "2.25rem",
										// 	width: "2.25rem",
										// },
									}}
								/>
								<StatValue variant="body2">{PreparePriceString(views_count ? views_count : 0)}</StatValue>
								<StatTitle variant="body2">{localLang.views}</StatTitle>
							</StatContainer>

							{/* Saves */}

							<StatContainer
								onClick={(e: any) => {
									e.preventDefault();
									e.stopPropagation();

									dispatch(setBusinessDashboardAnalyticsFilterListingTypeThunk("property"));
									dispatch(setBusinessDashboardAnalyticsFilterListingIdThunk(_id));
									dispatch(setBusinessDashboardAnalyticsFilterGraphTypeYThunk("saved_items"));
									dispatch(setBusinessDashboardAnalyticsFilterGraphDurationXThunk("all"));
									dispatch(setBusinessDashboardAnalyticsGraphSourceThunk("listing_saves"));
									dispatch(setBusinessDashboardCurrentScreenThunk("graph"));
									router.push(
										{
											pathname: router.pathname,
											query: {
												...router.query,
												screen: "graph",
											},
										},
										undefined,
										{ shallow: true },
									);
								}}
							>
								<SvgIcon
									component={SavedIcon}
									inheritViewBox={false}
									viewBox="0 0 36 36"
									width={24}
									height={24}
									sx={{
										height: "1.5rem",
										width: "1.5rem",
										fill: "none",
										// /* iphone 5 - 320 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
										// 	height: "2rem",
										// 	width: "2rem",
										// },
										// /* iphone se - 375 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
										// 	height: "2.25rem",
										// 	width: "2.25rem",
										// },
									}}
								/>
								<StatValue variant="body2">{PreparePriceString(saved_count ? saved_count : 0)}</StatValue>
								<StatTitle variant="body2">{localLang.saves}</StatTitle>
							</StatContainer>

							{/* Shares */}

							<StatContainer
								onClick={(e: any) => {
									e.preventDefault();
									e.stopPropagation();

									dispatch(setBusinessDashboardAnalyticsFilterListingTypeThunk("property"));
									dispatch(setBusinessDashboardAnalyticsFilterListingIdThunk(_id));
									dispatch(setBusinessDashboardAnalyticsFilterGraphTypeYThunk("shares"));
									dispatch(setBusinessDashboardAnalyticsFilterGraphDurationXThunk("all"));
									dispatch(setBusinessDashboardAnalyticsGraphSourceThunk("listing_shares"));
									dispatch(setBusinessDashboardCurrentScreenThunk("graph"));
									router.push(
										{
											pathname: router.pathname,
											query: {
												...router.query,
												screen: "graph",
											},
										},
										undefined,
										{ shallow: true },
									);
								}}
							>
								<SvgIcon
									component={SharesIcon}
									inheritViewBox={false}
									viewBox="0 0 25 24"
									width={24}
									height={24}
									sx={{
										height: "1.5rem",
										width: "1.5rem",
										fill: "none",
										// /* iphone 5 - 320 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 39)]: {
										// 	height: "2rem",
										// 	width: "2rem",
										// },
										// /* iphone se - 375 */
										// [theme.breakpoints.up(theme.breakpoints.values.xs + 94)]: {
										// 	height: "2.25rem",
										// 	width: "2.25rem",
										// },
									}}
								/>
								<StatValue variant="body2">{PreparePriceString(shares_count ? shares_count : 0)}</StatValue>
								<StatTitle variant="body2">{localLang.shares}</StatTitle>
							</StatContainer>
						</PropertyCardStatsContainer>
					) : null}

					{/* Dates */}

					{!show_only_listing_details ? (
						<RowContainer
							sx={{
								width: "100%",
								padding: "0rem 0.75rem 0rem 0.75rem",
							}}
						>
							{/* Posted on */}

							<RowContainer sx={{ gap: "0.25rem" }}>
								<EventOutlined
									sx={{
										fontSize: "1rem",
										color: "#B05911",
									}}
								/>
								<DateText variant="body2">
									{localLang.posted_on +
										" " +
										new Date(posted_on as string).toLocaleDateString("en-IN", {
											year: "numeric",
											month: "short",
											day: "numeric",
										})}
								</DateText>
							</RowContainer>

							{/* Updated on */}

							<RowContainer sx={{ gap: "0.25rem" }}>
								<UpdateOutlined
									sx={{
										fontSize: "1rem",
										color: "#B05911",
									}}
								/>
								<DateText variant="body2">
									{localLang.updated_on +
										" " +
										new Date(updated_on as string).toLocaleDateString("en-IN", {
											year: "numeric",
											month: "short",
											day: "numeric",
										})}
								</DateText>
							</RowContainer>
						</RowContainer>
					) : null}

					{/* CTA  */}

					{isEditor && !show_only_listing_details ? (
						<PropertyCardCTA
							propertyStatus={propertyStatus}
							session={session}
							business_profile_id={business_profile_id}
							broadcastData={broadcastData}
							propertyId={_id}
							transactionType={transaction_type}
							loading={loading}
							router={router}
							published_status={published_status}
							dispatch={dispatch}
							slug={slug}
							title={title}
							created_by={created_by}
							isEditor={isEditor}
							isBoosted={boosting ? boosting.active : false}
							profile_context={profile_context}
							is_recently_broadcasted={is_recently_broadcasted}
							is_broadcasted_before={is_broadcasted_before}
							time_left_to_broadcast_again={time_left_to_broadcast_again}
							broadcastFeatureDiscoveryTracker={broadcastFeatureDiscoveryTracker}
							ai_boosts={ai_boosts}
						/>
					) : null}
				</PropertyCardContainer>
			</PropertyLink>
		</React.Fragment>
	);
};

export default PropertyCard;
